import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { getRandomAciertoAvatar, getRandomErrorAvatar } from "../../utils/quizResultAvatars";
import ReactDOMServer from "react-dom/server";
import { IconHandClick, IconArrowNarrowRight } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import imageHintUsed from "../../assets/btnPistaOrange.png";
import unIntento from "../../assets/un-intento.png";
import dosIntentos from "../../assets/dos-intentos.png";
import imgPistaQuiz6 from "../../assets/hints/hint-concha.png";
import "../StartGame.css";
import "../Scenario2/Scenario2.css";

/** Clases extra del popup SweetAlert2 (tema burdeos Cobega). */
const SC4_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--cobega";
/** Pista: borde cian, avatar enmarcado, botón confirmar en teal (ver modals.css · modal-sg5--hint-sc2). */
const SC4_HINT_THEME_CLASS = `${SC4_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();

const SC4_SWAL_ICON_ARROW = ReactDOMServer.renderToStaticMarkup(
	<IconArrowNarrowRight size={50} color="#a41f35" stroke={2} />
);

const SC4_HINT_MARK_HTML = hintMarkPistaHtml();

const QUESTION_STEM =
	"Si eres conocedor de la historia de esta familia, seguramente estarás de acuerdo en que el buque insignia del grupo es Coca-Cola. Ayúdanos a recordar quién fue una figura clave en la obtención de la licencia de su distribución.";

const RETROALIMENTACION =
	"La respuesta correcta es Santiago Daurella i Rull";

const SCENARIO4_QUIZ1_BASE_OPTIONS = [
	{
		id: 1,
		label: "A",
		text: "Santiago Daurella i Rull",
		correct: true,
		img: null,
		explanation: "",
	},
	{
		id: 2,
		label: "B",
		text: "Josep Daurella Franco",
		correct: false,
		img: null,
		explanation: "",
	},
	{
		id: 3,
		label: "C",
		text: "Francisco Daurella Comadrán",
		correct: false,
		img: null,
		explanation: "",
	},
	{
		id: 4,
		label: "D",
		text: "José Daurella Sunyer",
		correct: false,
		img: null,
		explanation: "",
	},
];

function Scenario4Quiz1() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.QUIZ3;
	const SCENARIO_ID = SCENARIOS.SCENARIO_4;

	const [selectedOption, setSelectedOption] = useState(null);
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [attempt, setAttempt] = useState(1);
	const [blockedOptions, setBlockedOptions] = useState(new Set());
	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);
	const capitalAtQuizStartRef = useRef(null);

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		if (capitalAtQuizStartRef.current === null) capitalAtQuizStartRef.current = s;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const NUMBERATTEMPTS = 2;
	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -2;
	const CORRECT_ANSWER_POINTS_2 = 3;
	const WRONG_ANSWER_POINTS_2 = 0;

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "orange");

	const displayOptions = useMemo(() => shuffleQuizOptions(SCENARIO4_QUIZ1_BASE_OPTIONS), []);

	async function submitAnswer(finalScore, correct) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const base = capitalAtQuizStartRef.current;
			const capitalInicio = Number.isFinite(base) ? base : 0;
			const deltaParaApi = finalScore - capitalInicio;

			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: deltaParaApi,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO4_CONTEXT2,
				correct,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO4_CONTEXT2);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC4_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO4_CONTEXT2);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC4_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		const hint = `<div class="txtTexto1 mb-2">No busques una ciudad en el mapa: busca la concha que guía el camino hacia el Daurella que consiguió la licencia.</div><img src="${imgPistaQuiz6}" alt="" style="max-width:50%;height:auto;display:block;margin:0.5rem auto 0;margin-top:50px;" />`;

		if (hintUsed) {
			await modalHintShow({
				hint,
				hintMarkHtml: SC4_HINT_MARK_HTML,
				color: "orange",
				big: false,
				themePopupClass: SC4_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			image: getRandomEstasseguroAvatar(),
			imageHintUsed,
			hint,
			hintMarkHtml: SC4_HINT_MARK_HTML,
			color: "orange",
			big: false,
			themePopupClass: SC4_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(3);
		}
	};

	function checkAnswer() {
		if (!selectedOption) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 orange">Debes seleccionar una opción</div>`,
				themePopupClass: SC4_SWAL_POPUP_CLASS,
			});
			return;
		}

		const option = displayOptions.find((o) => o.id === selectedOption);
		if (!option) return;

		if (blockedOptions.has(option.id)) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="txt24 txt-red bold">Esa opción ya la intentaste y era incorrecta. Elijan otra.</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC4_SWAL_POPUP_CLASS,
			});
			return;
		}

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "orange",
			themePopupClass: SC4_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			if (option.correct) {
				const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
				const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
				const attemptDelta = base - hintThisAttempt;
				let newScore = pendingScore + attemptDelta;

				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				const capitalInicio = capitalAtQuizStartRef.current ?? 0;
				const netoEnEstaPregunta = newScore - capitalInicio;

				modalQuestionResolution({
					correct: true,
					question: `${option.label}. ${option.text}`,
					textHtml: `${buildQuizResultAvatarHtml(getRandomAciertoAvatar(), "correct", "orange")}
                      <div class="txt28 bold pt-4">Has ganado ${attemptDelta} M€ en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                      ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                      <div class="txt20 mt-2">Ganancia en esta pregunta: <b>${netoEnEstaPregunta}</b> M€</div>
                      ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "orange",
					themePopupClass: SC4_SWAL_POPUP_CLASS,
				}).then(() => {
					submitAnswer(newScore, true);
				});

				return;
			}

			if (attempt < NUMBERATTEMPTS) {
				const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
				const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
				let newScore = pendingScore + attemptDelta;
				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				const capitalInicio = capitalAtQuizStartRef.current ?? 0;
				const netoEnEstaPregunta = newScore - capitalInicio;

				modalQuestionResolution({
					correct: false,
					question: `${option.label}. ${option.text}`,
					textHtml: `${buildQuizResultAvatarHtml(getRandomErrorAvatar(), "incorrect", "orange")}
                      <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} M€ ${hintThisAttempt ? " (incluye -3M€ por pista)" : ""}</div>
                      <div class="txt22 mt-2">Te queda <b>1</b> intento. Elije otra opción.</div>
                      <div class="txt20 mt-2">Ganancia en esta pregunta: <b>${netoEnEstaPregunta}</b> M€</div>
                      <div class="txt18 mt-2 sc2-swal-hint-actions">Pulsa la flecha para intentar con otra opción.</div>`,
					customConfirmButtonHtml: SC4_SWAL_ICON_ARROW,
					confirmIsIconButton: true,
					color: "orange",
					themePopupClass: SC4_SWAL_POPUP_CLASS,
				}).then(() => {
					setPendingScore(newScore);
					setBlockedOptions((prev) => new Set(prev).add(option.id));
					setAttempt(attempt + 1);
					setSelectedOption(null);
					setIsSubmitting(false);
				});

				return;
			}

			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
			const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt;
			let newScore = pendingScore + attemptDelta;
			if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

			const capitalInicio = capitalAtQuizStartRef.current ?? 0;
			const netoEnEstaPregunta = newScore - capitalInicio;

			modalQuestionResolution({
				correct: false,
				question: `${option.label}. ${option.text}`,
				textHtml: `${buildQuizResultAvatarHtml(getRandomErrorAvatar(), "incorrect", "orange")}
                    <div class="txt28 bold pt-4">Han agotado los intentos</div>
                    ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3M€ por pista en este intento</div>` : ""}
                    <div class="txt20 mt-2">Ganancia en esta pregunta: <b>${netoEnEstaPregunta}</b> M€</div>
                    ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "orange",
				themePopupClass: SC4_SWAL_POPUP_CLASS,
			}).then(() => {
				submitAnswer(newScore, false);
			});
		});
	}

	const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand">
			<div className="start-game-inner start-game-inner--prep5">
				<span className="start-game-badge sg5-badge">
					<span className="start-game-badge-dot" aria-hidden />
					Escenario 3 · Coca-Cola
				</span>

				<div className="sg5-scroll">
					<header className="sg5-hero">
						<h1 className="sg5-title">Pregunta</h1>
						<p className="sg5-lead sc2-quiz-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={28}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>Selecciona una opción y pulsa Comprobar respuesta.</span>
						</p>
						<img
							src={remainingAttempts === 2 ? dosIntentos : unIntento}
							alt=""
							className="sc2-quiz-intentos"
						/>
					</div>

					<div className="sc2-quiz-options">
						{displayOptions.map((option) => {
							const isSelected = selectedOption === option.id;
							const isBlocked = blockedOptions.has(option.id);
							return (
								<button
									key={option.id}
									type="button"
									className={`sg5-aside-card sc2-quiz-option text-start ${isSelected ? "sc2-quiz-option--selected" : ""} ${isBlocked ? "sc2-quiz-option--blocked" : ""}`}
									onClick={() => !isBlocked && setSelectedOption(option.id)}
									disabled={isBlocked}
									aria-pressed={isSelected ? "true" : "false"}
									aria-label={`Opción ${option.label}`}
								>
									<div className="sc2-quiz-option-row">
										<span className="sc2-quiz-option-letter">{option.label}</span>
										<span className="sc2-quiz-option-text">{option.text}</span>
									</div>
									{isBlocked ? <div className="sc2-quiz-option-blocked-note">Bloqueada</div> : null}
								</button>
							);
						})}
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={hintUsed ? "Pista usada (-3 M€)" : "Usar pista (-3 M€)"}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? <span className="badge-penalty sc2-quiz-hint-badge">-3M€</span> : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default Scenario4Quiz1;
